import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogActions } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { TextField } from '@/components/ui/TextField';
import type { ApiError } from '@/services/http/api-error';
import { formFailure, type FormFailure } from '@/services/http/error-details';
import { toast } from '@/stores/toast.store';
import { checkConfirmation, checkNewPassword, PASSWORD_HINT } from '@/utils/password';
import { useChangePassword } from '../hooks/useChangePassword';

const FIELDS = ['currentPassword', 'newPassword'] as const;
type Field = (typeof FIELDS)[number] | 'confirmPassword';
type FieldErrors = Partial<Record<Field, string>>;
type Values = Record<Field, string>;

function validate(values: Values): FieldErrors {
  const errors: FieldErrors = {};
  if (!values.currentPassword) errors.currentPassword = 'Enter your current password.';
  const newPassword = checkNewPassword(values.newPassword);
  if (newPassword) errors.newPassword = newPassword;
  else if (values.newPassword === values.currentPassword) {
    errors.newPassword = 'Choose a password different from the current one.';
  }
  const confirmation = checkConfirmation(values.newPassword, values.confirmPassword);
  if (confirmation) errors.confirmPassword = confirmation;
  return errors;
}

function failureFrom(error: ApiError): FormFailure<Field> {
  if (error.code === 'ACCOUNT_LOCKED') {
    return {
      fields: {},
      general: {
        message: 'Too many wrong attempts; sign-in is locked for a while. Try again later.',
        requestId: error.requestId,
      },
    };
  }
  return formFailure(error, FIELDS, 'Could not change the password. Please try again.');
}

/** The signed-in user's own password. Opened from the header's user menu. */
export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const change = useChangePassword();
  const [values, setValues] = useState<Values>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [general, setGeneral] = useState<FormFailure<Field>['general']>();

  const field = (name: Field) => ({
    value: values[name],
    error: errors[name],
    onChange: (e: { target: { value: string } }) => {
      setValues((v) => ({ ...v, [name]: e.target.value }));
      setErrors((x) => ({ ...x, [name]: undefined }));
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGeneral(undefined);
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    change.mutate(
      { currentPassword: values.currentPassword, newPassword: values.newPassword },
      {
        onSuccess: () => {
          toast.success('Password changed. Your other sessions were signed out.');
          onClose();
        },
        onError: (error) => {
          const failure = failureFrom(error);
          setErrors(failure.fields);
          setGeneral(failure.general);
        },
      },
    );
  };

  return (
    <Dialog title="Change Password" onClose={onClose} busy={change.isPending}>
      <form noValidate onSubmit={onSubmit} className="flex flex-col gap-4">
        <TextField
          label="Current Password"
          required
          type="password"
          autoComplete="current-password"
          maxLength={128}
          {...field('currentPassword')}
        />
        <TextField
          label="New Password"
          required
          type="password"
          autoComplete="new-password"
          maxLength={128}
          hint={PASSWORD_HINT}
          {...field('newPassword')}
        />
        <TextField
          label="Confirm New Password"
          required
          type="password"
          autoComplete="new-password"
          maxLength={128}
          {...field('confirmPassword')}
        />
        <p className="text-xs text-muted">
          You stay signed in here; your other devices and browsers are signed out.
        </p>
        {general && <ErrorAlert message={general.message} requestId={general.requestId} />}
        <DialogActions>
          <Button variant="outline" onClick={onClose} disabled={change.isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={change.isPending}>
            Change Password
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
